/**
 * CreditPurchaseModal — credit pack and subscription purchase options.
 *
 * Two-column layout on desktop (sm breakpoint+), single column on mobile.
 * Left column: one-time credit packs. Right column: monthly subscription.
 * Buy/subscribe buttons create Stripe Checkout sessions and redirect.
 *
 * showOutOfCreditsMessage prop shows a contextual banner when the modal
 * was triggered by a 402 (out-of-credits) API error rather than the
 * "Buy credits" nav button.
 *
 * @spec SPEC:FEATURE:BILLING:CREDIT-PURCHASE
 */
import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { events } from '@/services/events'
import { api } from '@/lib/api'
import { logger } from '@/services/logger'
import { CREDIT_PACKS, SUBSCRIPTION_PLAN } from '@/lib/constants'

interface CreditPurchaseModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * When true, shows "You've run out of credits. Buy more to continue."
   * at the top of the modal. Only shown when opened via a 402 error,
   * not when opened from the "Buy credits" nav button.
   */
  showOutOfCreditsMessage?: boolean
}

export function CreditPurchaseModal({
  open,
  onOpenChange,
  showOutOfCreditsMessage = false,
}: CreditPurchaseModalProps) {
  const [loadingPackId, setLoadingPackId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleCheckout = async (packId: string) => {
    setLoadingPackId(packId)
    setError(null)
    try {
      events.track('pricing.checkout_initiated', { source: 'modal', packId })
      const result = await api.post<{ url: string }>('/stripe/checkout', { packId })
      window.location.href = result.url
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start checkout'
      setError(message)
      logger.error('checkout.failed', { packId, error: message })
      setLoadingPackId(null)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] rounded-lg border border-border bg-background p-6 shadow-lg focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 max-h-[90vh] overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold text-foreground">
            Buy Credits
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            Purchase credit packs or subscribe to a monthly plan to continue using Rocketships.
          </Dialog.Description>

          {showOutOfCreditsMessage && (
            <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3">
              <p className="text-sm font-medium text-destructive">
                You&apos;ve run out of credits. Buy more to continue.
              </p>
            </div>
          )}

          {error && (
            <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3">
              <p className="text-sm font-medium text-destructive">{error}</p>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Left column — credit packs */}
            <div className="space-y-3">
              <div>
                <h3 className="text-base font-semibold text-foreground">One-time packs</h3>
                <p className="text-sm text-muted-foreground">Buy credits when you need them.</p>
              </div>
              <div className="space-y-2">
                {CREDIT_PACKS.filter((p) => !('hidden' in p && p.hidden)).map(({ id, credits, priceUsd }) => (
                  <div
                    key={id}
                    className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {credits.toLocaleString()} credits
                      </p>
                      <p className="text-xs text-muted-foreground">${priceUsd} USD</p>
                    </div>
                    <Button
                      size="sm"
                      disabled={loadingPackId !== null}
                      onClick={() => void handleCheckout(id)}
                    >
                      {loadingPackId === id ? 'Loading...' : 'Buy'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column — subscription */}
            <div className="space-y-3">
              <div>
                <h3 className="text-base font-semibold text-foreground">Subscription</h3>
                <p className="text-sm text-muted-foreground">Everything you need to ship fast.</p>
              </div>
              <div className="rounded-lg border border-primary/30 bg-card p-5 space-y-4">
                <div>
                  <span className="text-3xl font-bold text-foreground">${SUBSCRIPTION_PLAN.priceUsd}</span>
                  <span className="text-base text-muted-foreground">/mo</span>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {SUBSCRIPTION_PLAN.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <span className="mt-0.5 text-primary leading-none">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  disabled={loadingPackId !== null}
                  onClick={() => void handleCheckout(SUBSCRIPTION_PLAN.id)}
                >
                  {loadingPackId === SUBSCRIPTION_PLAN.id ? 'Loading...' : 'Subscribe'}
                </Button>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-4">
            Credits expire after 12 months.
          </p>

          <div className="mt-6 flex justify-end">
            <Dialog.Close asChild>
              <Button variant="outline" size="sm">
                Close
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
